// Live quote table, the building block of W, WEI, CMDTY, CRYPTO and friends.

import { quotes, spark, type Quote, type Spark } from "./api";
import { h, rangeBar, spark as sparkSvg } from "./dom";
import * as f from "./fmt";
import type { Ctx } from "./registry";
import { aliasFor, display } from "./securities";
import { DataTable, type Col } from "./table";

export interface Row {
  sym: string;
  /// Display ticker; defaults to the Bloomberg-ish name.
  label?: string;
  /// Display name; defaults to the quote's short name.
  name?: string;
  /// Group heading rows: `{ sym: "", group: "Americas" }`.
  group?: string;
}

export type ColKey = "last" | "chg" | "pct" | "bidask" | "volume" | "range" | "mcap" | "pe" | "spark" | "ytd" | "time";

export interface MonitorOpts {
  cols: ColKey[];
  /// Decimal places for a symbol's price (FX wants 4).
  dp?: (sym: string) => number | undefined;
  /// Command for a row's number; default `SYM DES`.
  action?: (sym: string) => string;
  /// Extra columns appended after the standard ones.
  extra?: Col<Item>[];
  /// Text for the status line.
  source?: string;
}

export interface Item {
  row: Row;
  q?: Quote;
  spark?: number[];
  ytd?: number;
}

const QUOTE_MS = 15_000;
const SPARK_MS = 300_000;

/// A quote table for `rows`, kept live while `ctx` is mounted. Returns the
/// element; call `setRows` to change the symbol list.
export function quoteMonitor(ctx: Ctx, rows: Row[], opts: MonitorOpts): { el: HTMLElement; setRows(r: Row[]): void } {
  let current = rows;
  let qs = new Map<string, Quote>();
  let sp: Spark = {};
  let ytd: Spark = {};
  const has = (k: ColKey) => opts.cols.includes(k);
  const dp = (sym: string) => opts.dp?.(sym) ?? qs.get(sym)?.priceHint;

  const cols: Col<Item>[] = [
    { label: "Ticker", get: (i) => i.row.label ?? display(i.row.sym, i.q?.quoteType), cls: () => "amber" },
    { label: "Name", get: (i) => i.row.name ?? aliasFor(i.row.sym)?.name ?? i.q?.shortName ?? i.q?.longName ?? "", cls: () => "name" },
  ];
  if (has("last")) cols.push({ label: "Last", get: (i) => i.q?.regularMarketPrice, fmt: (v: number, i) => f.price(v, dp(i.row.sym)), num: true });
  if (has("chg")) cols.push({ label: "Net chg", get: (i) => i.q?.regularMarketChange, fmt: (v: number, i) => f.chg(v, dp(i.row.sym)), cls: (v) => f.cls(v), num: true });
  if (has("pct")) cols.push({ label: "%Chg", get: (i) => i.q?.regularMarketChangePercent, fmt: (v) => f.pct(v), cls: (v) => f.cls(v), num: true });
  if (has("bidask")) {
    cols.push({ label: "Bid", get: (i) => i.q?.bid || undefined, fmt: (v: number, i) => f.price(v, dp(i.row.sym)), num: true });
    cols.push({ label: "Ask", get: (i) => i.q?.ask || undefined, fmt: (v: number, i) => f.price(v, dp(i.row.sym)), num: true });
  }
  if (has("volume")) cols.push({ label: "Volume", get: (i) => i.q?.regularMarketVolume || undefined, fmt: (v) => f.big(v), num: true });
  if (has("mcap")) cols.push({ label: "Mkt cap", get: (i) => i.q?.marketCap, fmt: (v) => f.big(v), num: true });
  if (has("pe")) cols.push({ label: "P/E", get: (i) => i.q?.trailingPE, fmt: (v) => f.fixedN(v, 1), num: true });
  if (has("ytd")) cols.push({ label: "YTD", get: (i) => i.ytd, fmt: (v) => f.pct(v), cls: (v) => f.cls(v), num: true });
  if (has("range"))
    cols.push({
      label: "52w range",
      get: (i) => (i.q?.fiftyTwoWeekHigh ? ((i.q.regularMarketPrice ?? 0) - i.q.fiftyTwoWeekLow!) / (i.q.fiftyTwoWeekHigh - i.q.fiftyTwoWeekLow!) : undefined),
      fmt: (_v, i) => rangeBar(i.q?.fiftyTwoWeekLow, i.q?.fiftyTwoWeekHigh, i.q?.regularMarketPrice, 70),
      title: "Position within the 52-week high/low",
    });
  if (has("spark"))
    cols.push({ label: "Intraday", get: () => 0, sort: false, fmt: (_v, i) => (i.spark ? sparkSvg(i.spark, 90, 16, i.q?.regularMarketPreviousClose) : "") });
  if (has("time")) cols.push({ label: "Time", get: (i) => i.q?.regularMarketTime, fmt: (v) => f.stamp(v), cls: () => "dim", num: true });
  cols.push(...(opts.extra ?? []));

  const grouped = current.some((r) => r.group);
  const table = new DataTable<Item>(cols, {
    panel: ctx.panel,
    key: (i) => i.row.sym,
    group: grouped ? (i) => i.row.group ?? null : undefined,
    action: (i) => (i.row.sym ? (opts.action?.(i.row.sym) ?? `${i.row.sym} DES`) : null),
  });

  const syms = () => current.filter((r) => r.sym).map((r) => r.sym);
  const render = () => {
    table.set(
      current.map((row) => {
        const s = sp[row.sym];
        const y = ytd[row.sym];
        const q = qs.get(row.sym);
        const last = q?.regularMarketPrice ?? y?.c[y.c.length - 1];
        return {
          row,
          q,
          spark: s?.c,
          ytd: y?.prev && last ? (last / y.prev - 1) * 100 : undefined,
        };
      }),
    );
  };

  const loadQuotes = async () => {
    const list = syms();
    if (!list.length) return render();
    const chunks: string[][] = [];
    for (let i = 0; i < list.length; i += 60) chunks.push(list.slice(i, i + 60));
    const rs = await Promise.all(chunks.map((c) => quotes(c, ctx.signal)));
    if (ctx.signal.aborted) return;
    qs = new Map(rs.flatMap((r) => r.data).map((q) => [q.symbol, q]));
    const meta = rs[0]?.meta;
    ctx.panel.status(`${opts.source ?? "Yahoo"} · ${list.length} securities · refresh ${QUOTE_MS / 1000}s`, meta && { asof: meta.asof, stale: rs.some((r) => r.meta.stale) });
    render();
  };
  const loadSparks = async () => {
    const list = syms();
    if (!list.length) return;
    const [a, b] = await Promise.all([
      has("spark") ? spark(list, "1d", "5m", ctx.signal) : Promise.resolve({}),
      has("ytd") ? spark(list, "ytd", "1d", ctx.signal) : Promise.resolve({}),
    ]);
    if (ctx.signal.aborted) return;
    sp = a;
    ytd = b;
    render();
  };

  render();
  loadQuotes().catch((e) => ctx.panel.status(String(e.message ?? e)));
  loadSparks().catch(() => {});
  ctx.panel.every(QUOTE_MS, loadQuotes);
  ctx.panel.every(SPARK_MS, loadSparks);

  return {
    el: h("div", null, table.el),
    setRows(r: Row[]) {
      current = r;
      render();
      loadQuotes().catch(() => {});
      loadSparks().catch(() => {});
    },
  };
}
